/**
 * Dashboard page header — couple names lead, then ceremony date, then the
 * wedding "name" as a smaller subtitle. Names + roles are inline-editable so
 * brides can fix typos without leaving the dashboard.
 *
 * Display rules:
 *   - If both partner names are filled → "Riya & Arjun"
 *   - If only one filled → that one solo, the other shown as "Add partner".
 *   - If neither filled → fall back to the wedding name as the primary heading
 *     (older weddings created before this feature).
 */
import React, { useEffect, useState } from 'react';
import { Calendar, Download, EditPencil, Check, Xmark } from 'iconoir-react';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useWedding } from '../../contexts/WeddingContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'bride',   label: 'Bride' },
  { value: 'groom',   label: 'Groom' },
  { value: 'partner', label: 'Partner' },
];

function partnersDisplay(wedding) {
  const p1 = (wedding.partner1_name || '').trim();
  const p2 = (wedding.partner2_name || '').trim();
  if (p1 && p2) return `${p1} & ${p2}`;
  if (p1) return p1;
  if (p2) return p2;
  return null;
}

export default function DashboardHeader({ wedding }) {
  const { updateWedding } = useWedding();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    partner1_name: '', partner1_role: '',
    partner2_name: '', partner2_role: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!wedding) return;
    setDraft({
      partner1_name: wedding.partner1_name || '',
      partner1_role: wedding.partner1_role || '',
      partner2_name: wedding.partner2_name || '',
      partner2_role: wedding.partner2_role || '',
    });
  }, [wedding]);

  if (!wedding) return null;

  const couple = partnersDisplay(wedding);
  const heading = couple || wedding.name || 'Dashboard';
  const subtitle = couple ? wedding.name : null;
  const dateStr = wedding.wedding_date
    ? new Date(wedding.wedding_date).toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      })
    : null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateWedding(wedding.id, {
        partner1_name: draft.partner1_name || null,
        partner1_role: draft.partner1_role || null,
        partner2_name: draft.partner2_name || null,
        partner2_role: draft.partner2_role || null,
      });
      toast.success('Couple updated');
      setEditing(false);
    } catch (err) {
      toast.error('Failed to update couple');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="space-y-3" data-testid="couple-edit-form">
            <PartnerEditRow
              idx={1}
              draft={draft}
              setDraft={setDraft}
            />
            <PartnerEditRow
              idx={2}
              draft={draft}
              setDraft={setDraft}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="btn-primary text-xs flex items-center gap-1.5"
                data-testid="couple-save-btn"
              >
                <Check className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="btn-secondary text-xs flex items-center gap-1.5"
                data-testid="couple-cancel-btn"
              >
                <Xmark className="w-3.5 h-3.5" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1
                className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight"
                data-testid="dashboard-couple-heading"
              >
                {heading}
              </h1>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-muted hover:text-charcoal transition-colors"
                aria-label="Edit couple"
                data-testid="couple-edit-btn"
              >
                <EditPencil className="w-4 h-4" />
              </button>
            </div>
            {dateStr && (
              <p className="text-muted font-light mt-2 flex items-center gap-2" data-testid="dashboard-wedding-date">
                <Calendar className="w-4 h-4" />
                {dateStr}
              </p>
            )}
            {subtitle && (
              <p className="text-sm text-muted mt-1" data-testid="dashboard-wedding-name-subtitle">
                {subtitle}
              </p>
            )}
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
        <a
          href={`${BACKEND_URL}/api/export/${wedding.id}/csv`}
          className="btn-secondary flex items-center gap-2 no-underline"
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4" />
          CSV
        </a>
        <a
          href={`${BACKEND_URL}/api/export/${wedding.id}/pdf`}
          className="btn-secondary flex items-center gap-2 no-underline"
          data-testid="export-pdf-btn"
        >
          <Download className="w-4 h-4" />
          PDF
        </a>
      </div>
    </div>
  );
}

function PartnerEditRow({ idx, draft, setDraft }) {
  const nameKey = `partner${idx}_name`;
  const roleKey = `partner${idx}_role`;
  return (
    <div className="grid grid-cols-[1fr_140px] gap-2">
      <Input
        value={draft[nameKey]}
        onChange={(e) => setDraft({ ...draft, [nameKey]: e.target.value })}
        className="input-field h-10"
        placeholder={`Partner ${idx} name`}
        data-testid={`couple-edit-partner${idx}-name`}
      />
      <Select
        value={draft[roleKey]}
        onValueChange={(v) => setDraft({ ...draft, [roleKey]: v })}
      >
        <SelectTrigger className="input-field h-10" data-testid={`couple-edit-partner${idx}-role`}>
          <SelectValue placeholder="Role" />
        </SelectTrigger>
        <SelectContent>
          {ROLE_OPTIONS.map((r) => (
            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
